import { useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import { oneLine } from 'common-tags';

import { Button, Icon, Link } from '@/atoms';
import { Card } from '@/molecules';

import type { TypeTextColorVariant } from '@/components/types';
import type { FunctionComponent } from 'react';
import type { TypeHousingCardProps } from './types';

const VARIANT_MAPS: Record<TypeTextColorVariant, string> = {
  primary: ' text-primary-400',
  secondary: 'text-primary-400',
  alternative: 'text-white',
};

const PopUpBookingLavanda = dynamic(
  () => import('@/organisms/pop-up-booking-lavanda'),
  {
    ssr: false,
  }
);

/**
 * CardDondeEstamos
 */
export const HousingCard: FunctionComponent<TypeHousingCardProps> = ({
  className = '',
  galeryProps,
  caption,
  title,
  subtitle,
  description,
  characteristics,
  buttonText,
  linkBoton,
  textLink = '',
  accommodationSelect,
  buildingSelect,
  active = false,
  textColor = 'primary',
  orientation = 'column',
  characteristicsOrientation = 'column',
  contactData,
  email,
  phoneNumber,
  defaultBuilding,
  defaultApartments,
  province,
  landing = false,
}: TypeHousingCardProps) => {
  const { t, lang } = useTranslation('common');
  const [formOpen, setFormOpen] = useState(false);
  const popUpToggle = () => {
    setFormOpen(!formOpen);
  };
  return (
    <>
      <PopUpBookingLavanda
        apartmentsByBuilding={accommodationSelect}
        buildings={buildingSelect}
        show={formOpen}
        province={province}
        onClose={popUpToggle}
        defaultBuilding={defaultBuilding}
        defaultApartment={defaultApartments}
      />

      <Card
        orientation={orientation}
        multimediaProps={galeryProps}
        className={`rounded-md bg-white ${className}`}
        classNames={{ content: 'flex flex-col' }}
      >
        <div className="flex h-full flex-col justify-between">
          <div className={`${VARIANT_MAPS[textColor]} mb-4 flex flex-col`}>
            {caption && (
              <span className="text-caption text-caption--small mb-1">
                {caption}
              </span>
            )}
            <p className="text-heading text-heading--h4 mb-1">{title}</p>
            {subtitle && (
              <p className="text-body text-body--secondary mb-4 font-bold">
                {subtitle}
              </p>
            )}
            {description && (
              <p className="text-body text-body--primary font-light">
                {description}
              </p>
            )}
          </div>
          <div>
            {contactData ? (
              <div className="flex flex-col gap-2">
                {phoneNumber && (
                  <Link
                    underline
                    variant={textColor}
                    className="flex flex-row gap-1"
                    href={`tel:${phoneNumber}`}
                  >
                    <Icon src="Call" /> {phoneNumber}
                  </Link>
                )}
                {email && (
                  <Link
                    underline
                    variant={textColor}
                    className="flex flex-row gap-2"
                    href={`mailto:${email}`}
                  >
                    <Icon src="Autor" /> {email}
                  </Link>
                )}
              </div>
            ) : (
              <div
                className={oneLine`
                flex pb-2
                ${
                  characteristicsOrientation === 'row'
                    ? 'flex-row justify-between '
                    : 'flex-col gap-2'
                }
              `}
              >
                {characteristics?.map((element, index) => (
                  <div
                    className={`flex flex-row items-center gap-2 ${VARIANT_MAPS[textColor]}`}
                    key={index}
                  >
                    <Icon height={20} src={element.icon} />
                    <span>{element.text}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="h-px w-full bg-primary-500 opacity-5" />
            <div className=" flex flex-row justify-between pt-3">
              {active ? (
                <>
                  {buttonText && linkBoton ? (
                    <>
                      <NextLink
                        href={landing ? '#Contacto' : linkBoton ?? '#'}
                        prefetch={false}
                        passHref
                        {...(!landing && {
                          locale: linkBoton.split('/')[1],
                        })}
                      >
                        <Button
                          className="max-sm:hidden w-auto !px-5 !py-2"
                          variant="outline"
                          size="large"
                          as="a"
                        >
                          {buttonText}
                        </Button>
                      </NextLink>
                      <NextLink
                        href={linkBoton ?? '#'}
                        prefetch={false}
                        passHref
                      >
                        <Button
                          className="w-auto sm:hidden"
                          variant="outline"
                          size="small"
                          as="a"
                        >
                          {buttonText}
                        </Button>
                      </NextLink>
                    </>
                  ) : (
                    <>
                      <Button
                        className="max-sm:hidden w-auto !px-5 !py-2"
                        variant="outline"
                        size="large"
                        disabled={true}
                      >
                        {buttonText}
                      </Button>

                      <Button
                        className="w-auto sm:hidden"
                        variant="outline"
                        size="small"
                        disabled={true}
                      >
                        {buttonText}
                      </Button>
                    </>
                  )}
                  {textLink && (
                    <Link
                      className={`${landing ? 'hidden' : ''} text-links--small`}
                      underline={false}
                      arrow={false}
                      variant="primary"
                      as="button"
                      onClick={popUpToggle}
                    >
                      {textLink}
                    </Link>
                  )}
                </>
              ) : (
                <>
                  <Button disabled variant="outline" size="large" as="button">
                    {t('components.organisms.housingCard.comingSoon')}
                  </Button>
                  <NextLink
                    href={landing ? '#contacto' : `/${lang}/contacto`}
                    passHref
                    locale={lang}
                    prefetch={false}
                  >
                    <Link arrow={false} variant="primary">
                      {t('components.organisms.housingCard.letMeKnow')}
                    </Link>
                  </NextLink>
                </>
              )}
            </div>
          </div>
        </div>
      </Card>
    </>
  );
};

HousingCard.displayName = 'CardDondeEstamos';
